import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import BrandsSection from '../components/BrandsSection';
import InstagramPostsSection from '../components/InstagramPostsSection';
import ServicesSection from '../components/ServicesSection';
import TalentModal from '../components/TalentModal';
import CountryFlag from '../components/CountryFlag';
import { getTalentos, sendContacto } from '../services/api';
import { sendInfluencerLead } from '../services/influs';
import VerticalFilterSelect from '../components/VerticalFilterSelect';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Users2, Instagram } from 'lucide-react';
import { FaTiktok, FaYoutube, FaLinkedin } from 'react-icons/fa';

const STATS = [
  { value: 'LATAM', label: 'Presencia en toda la región' },
  { value: '+10K', label: 'Campañas ejecutadas' },
  { value: '+100', label: 'Marcas internacionales' },
];

const StatsRow = () => {
  return (
    <div className="flex flex-wrap items-end gap-10 md:gap-16">
      {STATS.map((stat) => (
        <div key={stat.value} className="stat-item">
          <span className="text-5xl md:text-7xl font-bold block leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#4A154B' }}>
            {stat.value}
          </span>
          <span className="text-sm md:text-base uppercase tracking-wider mt-2 block" style={{ fontFamily: "'DM Sans', sans-serif", color: 'rgba(0,0,0,0.4)' }}>
            {stat.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// Renders a single card (image + gradient + label + socials) — used both for the
// stable base layer and for the incoming overlay so their layouts stay identical.
const TalentCardBody = ({ talento, testId }) => (
  <>
    <img src={talento.image} alt={talento.name} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
    <CountryFlag talento={talento} className="absolute top-2 right-2 md:top-3 md:right-3 z-10" />
    <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4 space-y-1 md:space-y-3">
      <h3 className="font-bold text-xs md:text-base uppercase text-center drop-shadow-lg" style={{ color: '#F5F5DC' }} data-testid={testId}>
        {talento.name}
      </h3>
      <div className="flex justify-center items-center space-x-2 md:space-x-3">
        <a href={talento.instagram_url || '#'} target={talento.instagram_url ? '_blank' : '_self'} rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); if (!talento.instagram_url) e.preventDefault(); }} className={`w-6 h-6 md:w-9 md:h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white md:border-2 flex items-center justify-center text-white hover:bg-white hover:text-pink-500 transition-all duration-300 ${!talento.instagram_url && 'opacity-50 cursor-not-allowed'}`}>
          <Instagram size={12} className="md:hidden" /><Instagram size={16} className="hidden md:block" />
        </a>
        <a href={talento.tiktok_url || '#'} target={talento.tiktok_url ? '_blank' : '_self'} rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); if (!talento.tiktok_url) e.preventDefault(); }} className={`w-6 h-6 md:w-9 md:h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white md:border-2 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300 ${!talento.tiktok_url && 'opacity-50 cursor-not-allowed'}`}>
          <FaTiktok size={10} className="md:hidden" /><FaTiktok size={16} className="hidden md:block" />
        </a>
      </div>
    </div>
  </>
);

// Each slot has a stable base talent (always at opacity 1) and optionally an
// incoming overlay talent that crossfades in over 400ms. Because image + label
// + socials are all inside the SAME overlay <div> that owns the opacity
// transition, they are guaranteed to be in sync at every frame — the reported
// "photo A + name of B" bug is impossible by construction.
const TalentShowcase = ({ talentos, onTalentClick }) => {
  const INITIAL = [0, 1, 2, 3].map((i) => ({ stable: i, incoming: null, visible: false, dominant: false }));
  const [slots, setSlots] = useState(INITIAL);
  const nextIndexRef = useRef(4);
  const slotToSwapRef = useRef(0);

  useEffect(() => {
    if (talentos.length <= 4) return;
    // Reset on data change so nextIndexRef stays in range
    nextIndexRef.current = 4;
    slotToSwapRef.current = 0;

    const rafTimers = new Set();
    const fadeTimers = new Set();

    const interval = setInterval(() => {
      const s = slotToSwapRef.current;
      const nextIdx = nextIndexRef.current % talentos.length;

      // Phase 1: mount overlay at opacity 0
      setSlots((prev) => {
        const c = [...prev];
        c[s] = { ...c[s], incoming: nextIdx, visible: false, dominant: false };
        return c;
      });

      // Phase 2: next paint frame → flip opacity to 1 so CSS transitions from 0
      const raf1 = requestAnimationFrame(() => {
        const raf2 = requestAnimationFrame(() => {
          setSlots((prev) => {
            const c = [...prev];
            if (c[s].incoming === nextIdx) c[s] = { ...c[s], visible: true };
            return c;
          });
          rafTimers.delete(raf2);
        });
        rafTimers.add(raf2);
        rafTimers.delete(raf1);
      });
      rafTimers.add(raf1);

      // Phase 2.5: halfway through the fade (~200ms), the overlay crosses opacity
      // 0.5 — from this point on the user perceives the overlay talent as the
      // dominant one, so clicks and the visible testid must follow it.
      const tHalf = setTimeout(() => {
        setSlots((prev) => {
          const c = [...prev];
          if (c[s].incoming === nextIdx) c[s] = { ...c[s], dominant: true };
          return c;
        });
        fadeTimers.delete(tHalf);
      }, 200);
      fadeTimers.add(tHalf);

      // Phase 3: after 400ms, atomically promote incoming → stable and unmount overlay
      const tEnd = setTimeout(() => {
        setSlots((prev) => {
          const c = [...prev];
          if (c[s].incoming === nextIdx) {
            c[s] = { stable: nextIdx, incoming: null, visible: false, dominant: false };
          }
          return c;
        });
        fadeTimers.delete(tEnd);
      }, 420);
      fadeTimers.add(tEnd);

      nextIndexRef.current = (nextIndexRef.current + 1) % talentos.length;
      slotToSwapRef.current = (slotToSwapRef.current + 1) % 4;
    }, 720);

    return () => {
      clearInterval(interval);
      rafTimers.forEach(cancelAnimationFrame);
      fadeTimers.forEach(clearTimeout);
    };
  }, [talentos]);

  if (talentos.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        No hay talentos en esta categoría
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
      {slots.map(({ stable, incoming, visible, dominant }, slotIdx) => {
        const baseT = talentos[stable] || talentos[0];
        const overlayT = incoming != null ? talentos[incoming] : null;
        if (!baseT) return null;
        // Click and the "primary" testid follow the layer the user actually sees.
        // The overlay only becomes dominant after ~200ms (half of the 400ms fade).
        const isOverlayPrimary = !!overlayT && dominant;
        const primaryTalent = isOverlayPrimary ? overlayT : baseT;
        return (
          <div
            key={`slot-${slotIdx}`}
            className="group relative overflow-hidden rounded-xl shadow-lg cursor-pointer"
            onClick={() => onTalentClick && onTalentClick(primaryTalent)}
          >
            <div className="aspect-[4/5] relative overflow-hidden">
              {/* Base layer: stable talent, always opacity 1 */}
              <TalentCardBody talento={baseT} testId={isOverlayPrimary ? undefined : `talent-name-slot-${slotIdx}`} />

              {/* Overlay layer: crossfade in with matching image + label together */}
              {overlayT && (
                <div
                  key={`overlay-${slotIdx}-${incoming}`}
                  className="absolute inset-0"
                  style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease-in-out' }}
                >
                  <TalentCardBody talento={overlayT} testId={isOverlayPrimary ? `talent-name-slot-${slotIdx}` : undefined} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const QueHacemosColumns = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`grid grid-cols-1 sm:grid-cols-2 gap-12 md:gap-20 ${visible ? 'que-hacemos-visible' : ''}`}>
      <div className="slide-up-1" data-testid="que-hacemos-col-1">
        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#FFFDEB' }}>Potenciamos</h2>
        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#FFFDEB' }}>lo que hace único</h2>
        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#FFFDEB' }}>a cada <span style={{ color: '#F4A1C8' }}>talento</span></h2>
      </div>
      <div className="slide-up-2" data-testid="que-hacemos-col-2">
        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#FFFDEB' }}>Conectamos</h2>
        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#FFFDEB' }}>marcas con</h2>
        <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#F4A1C8' }}>audiencias reales</h2>
      </div>
    </div>
  );
};

const NewHome = () => {
  const { toast } = useToast();
  const [talentos, setTalentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('marca');
  const [showForm, setShowForm] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [talentoVerticales, setTalentoVerticales] = useState([]);
  // Controlled social handles + followers so we can validate that at least ONE
  // network is fully completed before allowing submission.
  const [socials, setSocials] = useState({
    instagram_handle: '', instagram_followers: '',
    tiktok_handle: '',    tiktok_followers: '',
    youtube_handle: '',   youtube_followers: '',
  });
  const [socialTouched, setSocialTouched] = useState(false);
  const setSocial = (k, v) => {
    setSocials((s) => ({ ...s, [k]: v }));
    // Once the user has typed anything in the socials block, keep showing
    // the validation banner until at least one row is complete.
    setSocialTouched(true);
  };
  const clean = (v) => (v || '').trim();
  const hasCompleteSocial = (
    (clean(socials.instagram_handle) && clean(socials.instagram_followers)) ||
    (clean(socials.tiktok_handle)    && clean(socials.tiktok_followers)) ||
    (clean(socials.youtube_handle)   && clean(socials.youtube_followers))
  );
  const navigate = useNavigate();

  // Selecting a vertical on the home page navigates to /talentos with the filter applied
  const handleVerticalChange = (pair) => {
    const params = new URLSearchParams();
    const [v1, v2] = pair || [];
    if (v1 && v1 !== '__ALL__') params.set('v1', v1);
    if (v2 && v2 !== '__ALL__') params.set('v2', v2);
    const qs = params.toString();
    navigate(qs ? `/talentos?${qs}` : '/talentos');
  };

  const heroVideoUrl = "https://customer-assets.emergentagent.com/job_583c071d-e433-4811-ad6c-bde7a5e19535/artifacts/9yd67b7e_Presentaci%C3%B3n%20corporativa%20de%20aventuras%20de%20relax%20%281%29.mp4";

  const fetchTalentos = useCallback(async () => {
    try {
      const data = await getTalentos();
      setTalentos(data);
    } catch (err) {
      console.error('Failed to fetch talentos:', err);
    }
  }, []);

  useEffect(() => {
    fetchTalentos();
  }, [fetchTalentos]);

  const handleContactSubmit = async (e, tipo) => {
    e.preventDefault();

    if (tipo === 'talento') {
      // Enforce: at least ONE social row must have BOTH handle AND followers.
      if (!hasCompleteSocial) {
        setSocialTouched(true);
        // scroll the socials block into view so the user sees the hint
        try { document.getElementById('influencer-socials-block')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
        return;
      }
    }

    setLoading(true);

    const formData = new FormData(e.target);

    try {
      if (tipo === 'talento') {
        // Lead form for micro-influencers: save into MongoDB CRM (no email)
        const edadRaw = formData.get('edad');
        const payload = {
          nombre: formData.get('nombre') || '',
          email: formData.get('email') || '',
          pais: formData.get('pais') || '',
          ciudad: formData.get('ciudad') || '',
          celular: formData.get('celular') || '',
          edad: edadRaw ? parseInt(edadRaw, 10) : null,
          instagram_handle: socials.instagram_handle.trim(),
          instagram_followers: socials.instagram_followers.trim(),
          tiktok_handle: socials.tiktok_handle.trim(),
          tiktok_followers: socials.tiktok_followers.trim(),
          youtube_handle: socials.youtube_handle.trim(),
          youtube_followers: socials.youtube_followers.trim(),
          verticales: talentoVerticales,
          mensaje: formData.get('mensaje') || '',
        };
        await sendInfluencerLead(payload);
        toast({
          title: '¡Solicitud enviada!',
          description: 'Gracias por aplicar. Revisaremos tu perfil y te contactaremos pronto.',
        });
        e.target.reset();
        setTalentoVerticales([]);
        setSocials({
          instagram_handle: '', instagram_followers: '',
          tiktok_handle: '',    tiktok_followers: '',
          youtube_handle: '',   youtube_followers: '',
        });
        setSocialTouched(false);
      } else {
        // Marca/agencia flow: keep current behavior (Resend email + contactos collection)
        const data = {
          nombre: formData.get('nombre'),
          empresa: formData.get('empresa') || '',
          email: formData.get('email'),
          asunto: formData.get('asunto'),
          mensaje: formData.get('mensaje') || '',
          pais: formData.get('pais') || '',
          telefono: formData.get('telefono') || '',
          web: formData.get('web') || '',
          tipo: tipo,
        };
        await sendContacto(data);
        toast({
          title: '¡Mensaje enviado!',
          description: `Gracias por contactarnos. Te responderemos pronto a ${data.email}`,
        });
        e.target.reset();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un problema al enviar tu mensaje. Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const VERTICAL_OPTIONS = [
    'Beauty & Make Up', 'Dance', 'Foodie', 'Lifestyle', 'Fitness', 'Humor',
    'Moda', 'Family', 'Travel', 'Gaming', 'Tech', 'Sport', 'Otros',
  ];
  const toggleVertical = (v) => {
    setTalentoVerticales((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );
  };

  return (
    <div className="min-h-screen">
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap');
        .crayon-underline {
          position: relative;
          display: inline-block;
        }
        .crayon-underline::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: -8%;
          width: 115%;
          height: 14px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(244, 161, 200, 0.7) 10%, 
            rgba(244, 161, 200, 0.85) 30%,
            rgba(244, 161, 200, 0.75) 50%,
            rgba(244, 161, 200, 0.9) 70%,
            rgba(244, 161, 200, 0.7) 90%,
            transparent 100%
          );
          border-radius: 0% 50% 50% 0% / 0% 100% 100% 0%;
          transform: rotate(-2deg) skewX(-5deg);
          z-index: 1;
          clip-path: ellipse(55% 70% at 50% 30%);
        }
        .crayon-underline-dark::after {
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(244, 161, 200, 0.85) 10%, 
            rgba(244, 161, 200, 0.95) 30%,
            rgba(244, 161, 200, 0.9) 50%,
            rgba(244, 161, 200, 0.95) 70%,
            rgba(244, 161, 200, 0.85) 90%,
            transparent 100%
          );
        }
      `}</style>

      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-white">
        {/* Background Video - vertical on mobile, horizontal on desktop */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Container with responsive aspect ratio */}
          <div 
            className="relative h-[90%] max-w-[90vw] md:h-full md:w-full md:max-w-none"
            style={{ aspectRatio: 'var(--video-aspect)' }}
          >
            <style>{`
              @media (max-width: 767px) {
                :root { --video-aspect: 9/16; }
              }
              @media (min-width: 768px) {
                :root { --video-aspect: auto; }
              }
            `}</style>
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover rounded-lg md:rounded-none shadow-xl md:shadow-none"
              data-testid="hero-video"
            >
              <source src={heroVideoUrl} type="video/mp4" />
              Tu navegador no soporta videos HTML5.
            </video>
            
            {/* Social Media Icons - vertical en el margen derecho del video */}
            <div className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 flex flex-col space-y-3 md:space-y-4">
              <a
                href="https://www.tiktok.com/@talengers_ok"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/30 backdrop-blur-sm border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300"
                title="TikTok"
              >
                <FaTiktok size={16} className="md:hidden" />
                <FaTiktok size={20} className="hidden md:block" />
              </a>
              <a
                href="https://www.instagram.com/talengers.ok/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/30 backdrop-blur-sm border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-pink-500 transition-all duration-300"
                title="Instagram"
              >
                <Instagram size={16} className="md:hidden" />
                <Instagram size={20} className="hidden md:block" />
              </a>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/30 backdrop-blur-sm border-2 border-white flex items-center justify-center text-white opacity-50 cursor-not-allowed"
                title="YouTube - Próximamente"
              >
                <FaYoutube size={16} className="md:hidden" />
                <FaYoutube size={20} className="hidden md:block" />
              </a>
              <a
                href="https://www.linkedin.com/company/talengersok/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/30 backdrop-blur-sm border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-blue-600 transition-all duration-300"
                title="LinkedIn"
              >
                <FaLinkedin size={16} className="md:hidden" />
                <FaLinkedin size={20} className="hidden md:block" />
              </a>
            </div>
          </div>
        </div>

        {/* Logo dentro de la barra */}
        <div className="fixed top-0 left-8 md:left-24 z-[60] h-24 flex items-center">
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_e5e0b548-5d46-45b3-9c33-35f27ba77bc9/artifacts/d98c346ff4425b85_Logo%202026%20D2%20alta.webp"
            alt="Talengers Logo"
            className="h-10 md:h-14 w-auto drop-shadow-lg"
            data-testid="hero-logo"
          />
        </div>

        {/* Frase en el cuadrante inferior izquierdo */}
        <div className="absolute bottom-32 md:bottom-36 left-16 md:left-40 z-10">
          <h2 className="text-3xl md:text-5xl drop-shadow-md text-white text-center leading-tight uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
            Talentos con identidad,<br/><span className="crayon-underline">Campañas con impacto</span>
          </h2>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-3 bg-white rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Watermark Background Container - desde los círculos hasta el final */}
      <div className="relative">
        {/* Watermark logos de fondo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          {/* Logo 1 - arriba derecha, parcialmente visible */}
          <img
            src="https://customer-assets.emergentagent.com/job_583c071d-e433-4811-ad6c-bde7a5e19535/artifacts/6o57pqsn_Solo%20Logo%20Mail.png"
            alt=""
            className="absolute -right-32 top-20 w-[1006px] h-[1006px] opacity-[0.03]"
          />
          {/* Logo 2 - centro izquierda */}
          <img
            src="https://customer-assets.emergentagent.com/job_583c071d-e433-4811-ad6c-bde7a5e19535/artifacts/6o57pqsn_Solo%20Logo%20Mail.png"
            alt=""
            className="absolute -left-40 top-[800px] w-[1207px] h-[1207px] opacity-[0.03]"
          />
          {/* Logo 3 - abajo derecha, parcialmente visible */}
          <img
            src="https://customer-assets.emergentagent.com/job_583c071d-e433-4811-ad6c-bde7a5e19535/artifacts/6o57pqsn_Solo%20Logo%20Mail.png"
            alt=""
            className="absolute -right-48 top-[1800px] w-[1104px] h-[1104px] opacity-[0.03]"
          />
        </div>

        {/* Verticals Section removed */}

      {/* About Section - Quiénes Somos */}
      <section id="quienes-somos" className="py-12 md:py-16" style={{ backgroundColor: '#FFFDEB' }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-10 h-[3px]" style={{ backgroundColor: '#742263' }} />
            <span className="text-sm md:text-base font-semibold tracking-[0.3em] uppercase" style={{ fontFamily: "'DM Sans', sans-serif", color: '#742263' }}>
              Quiénes Somos
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#4A154B' }}>
              Somos una agencia regional de management de <span style={{ color: '#F4A1C8' }}>artistas e influencers</span>
            </h2>
            <div className="flex flex-col justify-center">
              <div className="w-12 h-[3px] mb-6" style={{ backgroundColor: '#F4A1C8' }} />
              <p className="text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, color: '#4A154B' }}>
                Creemos en el poder de las historias bien contadas. Potenciamos la esencia de nuestros creadores para construir comunidades fieles y colaboraciones auténticas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 md:py-10 relative overflow-hidden" style={{ backgroundColor: '#FFFDEB' }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Stats row with looping slide-in animation */}
          <style>{`
            @keyframes slideInLoop {
              0% { opacity: 0; transform: translateX(60px); }
              20% { opacity: 1; transform: translateX(0); }
              80% { opacity: 1; transform: translateX(0); }
              100% { opacity: 0; transform: translateX(-60px); }
            }
            .stat-item {
              animation: slideInLoop 3s ease-in-out infinite;
            }
            .stat-item:nth-child(1) { animation-delay: 0s; }
            .stat-item:nth-child(2) { animation-delay: 0.3s; }
            .stat-item:nth-child(3) { animation-delay: 0.6s; }
          `}</style>
          <StatsRow />
        </div>
      </section>

      {/* Qué Hacemos Section */}
      <section className="py-12 md:py-16 relative overflow-hidden" style={{ backgroundColor: '#742263' }}>
        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(50px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .que-hacemos-visible .slide-up-1 {
            animation: slideUp 0.8s ease-out forwards;
          }
          .que-hacemos-visible .slide-up-2 {
            animation: slideUp 0.8s ease-out 0.4s forwards;
          }
          .slide-up-1, .slide-up-2 { opacity: 0; }
        `}</style>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-10 h-[3px]" style={{ backgroundColor: '#F4A1C8' }} />
            <span className="text-sm md:text-base font-semibold tracking-[0.3em] uppercase" style={{ fontFamily: "'DM Sans', sans-serif", color: '#F4A1C8' }}>
              Qué Hacemos
            </span>
          </div>
          <QueHacemosColumns />
        </div>
      </section>

      {/* Talentos Section */}
      <section id="talentos" className="pt-8 pb-20" style={{ backgroundColor: '#FFFDEB' }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Label */}
          <div className="flex items-center gap-4 mb-10">
            <div className="w-10 h-[3px]" style={{ backgroundColor: '#742263' }} />
            <span className="text-sm md:text-base font-semibold tracking-[0.3em] uppercase" style={{ fontFamily: "'DM Sans', sans-serif", color: '#742263' }}>
              Nuestro Roster
            </span>
          </div>

          {/* Title + Button row */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-6">
            <h2
              className="text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif", color: 'rgba(74,21,75,0.18)' }}
            >
              Los Talentos
            </h2>
            <Link to="/talentos" data-testid="ver-roster-button">
              <div
                className="inline-flex items-center gap-3 px-8 py-4 text-base md:text-lg font-bold uppercase tracking-wider transition-all duration-300"
                style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: '#F4A1C8', color: '#4A154B' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e882ad'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F4A1C8'}
              >
                Ver Roster Completo
                <span>&rarr;</span>
              </div>
            </Link>
          </div>

          {/* Vertical filter — 2 combinable dropdowns. Selecting any of them
              navigates to /talentos with the filter pre-applied. */}
          <div className="mb-10">
            <VerticalFilterSelect
              talentos={talentos}
              value={['__ALL__', '__ALL__']}
              onChange={handleVerticalChange}
              accentColor="#2A9D8F"
            />
          </div>

          <TalentShowcase talentos={talentos} onTalentClick={setSelectedTalent} />
        </div>
      </section>

      {/* Instagram Posts Section */}
      <InstagramPostsSection />

      {/* Contact Section */}
      <section id="contacto" className="py-12" style={{ backgroundColor: '#FFFDEB' }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-10 h-[3px]" style={{ backgroundColor: '#742263' }} />
            <span className="text-sm md:text-base font-semibold tracking-[0.3em] uppercase" style={{ fontFamily: "'DM Sans', sans-serif", color: '#742263' }}>
              Contacto
            </span>
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl leading-none tracking-tight mb-10" style={{ fontFamily: "'Bebas Neue', sans-serif", color: 'rgba(74,21,75,0.18)' }}>
            Hablemos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            <div className="aspect-[4/3] md:aspect-[3/2] p-8 md:p-12 flex flex-col justify-between" style={{ backgroundColor: '#742263' }} data-testid="cta-talento-box">
              <h3 className="text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#fff' }}>
                ¿Tenés el talento? Nosotros la estrategia.
              </h3>
              <div>
                <button onClick={() => { setActiveTab('talento'); setShowForm(true); setTimeout(() => document.getElementById('contacto-form')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="inline-flex items-center gap-2 px-6 py-3 border-2 text-sm md:text-base font-semibold uppercase tracking-wider transition-all duration-300 hover:bg-white/10" style={{ fontFamily: "'DM Sans', sans-serif", borderColor: '#fff', color: '#fff' }} data-testid="cta-talento-button">
                  Contactanos <span>&rarr;</span>
                </button>
              </div>
            </div>
            <div className="aspect-[4/3] md:aspect-[3/2] p-8 md:p-12 flex flex-col justify-between" style={{ backgroundColor: '#4A154B' }} data-testid="cta-marca-box">
              <h3 className="text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: '#fff' }}>
                ¿Buscás talentos? Te ayudamos.
              </h3>
              <div>
                <button onClick={() => { setActiveTab('marca'); setShowForm(true); setTimeout(() => document.getElementById('contacto-form')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="inline-flex items-center gap-2 px-6 py-3 border-2 text-sm md:text-base font-semibold uppercase tracking-wider transition-all duration-300 hover:bg-white/10" style={{ fontFamily: "'DM Sans', sans-serif", borderColor: '#fff', color: '#fff' }} data-testid="cta-marca-button">
                  Contactanos <span>&rarr;</span>
                </button>
              </div>
            </div>
          </div>
          {showForm && (
          <div id="contacto-form">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-3xl mx-auto">
              <style>{`
                .tab-marca[data-state="active"] { background-color: #4A154B !important; color: white !important; }
                .tab-talento[data-state="active"] { background-color: #742263 !important; color: white !important; }
              `}</style>
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-white p-2 h-auto gap-2 shadow-lg rounded-xl">
                <TabsTrigger value="marca" className="tab-marca text-gray-500 py-4 px-2 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:text-gray-700 whitespace-normal leading-tight">
                  <Users2 className="w-5 h-5 mr-2" />Soy una Marca/Agencia
                </TabsTrigger>
                <TabsTrigger value="talento" className="tab-talento text-gray-500 py-4 px-2 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:text-gray-700 whitespace-normal leading-tight">
                  <Sparkles className="w-5 h-5 mr-2" />Soy un Talento
                </TabsTrigger>
              </TabsList>
              <TabsContent value="marca">
                <Card className="bg-white border-gray-200 shadow-xl"><CardContent className="pt-6">
                  <form onSubmit={(e) => handleContactSubmit(e, 'marca')} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label htmlFor="marca-nombre" className="text-gray-700">Tu nombre *</Label><Input id="marca-nombre" name="nombre" placeholder="Nombre y apellido" required className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" data-testid="marca-input-nombre" /></div>
                      <div className="space-y-2"><Label htmlFor="marca-empresa" className="text-gray-700">Empresa / Agencia *</Label><Input id="marca-empresa" name="empresa" placeholder="Nombre de tu empresa o agencia" required className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" data-testid="marca-input-empresa" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label htmlFor="marca-email" className="text-gray-700">Email *</Label><Input id="marca-email" name="email" type="email" placeholder="tu@email.com" required className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" /></div>
                      <div className="space-y-2"><Label htmlFor="marca-telefono" className="text-gray-700">Teléfono</Label><Input id="marca-telefono" name="telefono" placeholder="+54 9 11 ..." className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" data-testid="marca-input-telefono" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label htmlFor="marca-pais" className="text-gray-700">País</Label><Input id="marca-pais" name="pais" placeholder="Ej: Argentina" className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" data-testid="marca-input-pais" /></div>
                      <div className="space-y-2"><Label htmlFor="marca-web" className="text-gray-700">Web / RRSS</Label><Input id="marca-web" name="web" placeholder="tumarca.com o @tumarca" className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" data-testid="marca-input-web" /></div>
                    </div>
                    <div className="space-y-2"><Label htmlFor="marca-asunto" className="text-gray-700">Asunto *</Label><Input id="marca-asunto" name="asunto" placeholder="¿En qué podemos ayudarte?" required className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400" /></div>
                    <div className="space-y-2"><Label htmlFor="marca-mensaje" className="text-gray-700">Mensaje (opcional)</Label><Textarea id="marca-mensaje" name="mensaje" placeholder="Cuéntanos sobre tu proyecto o campaña..." rows={5} className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 resize-none" /></div>
                    <Button type="submit" disabled={loading} className="w-full font-semibold py-6 text-lg" style={{ backgroundColor: '#F4A1C8', color: '#4A154B' }}>{loading ? 'Enviando...' : 'Enviar Mensaje'}</Button>
                  </form>
                </CardContent></Card>
              </TabsContent>
              <TabsContent value="talento">
                <Card className="bg-white border-gray-200 shadow-xl"><CardContent className="pt-6">
                  <form onSubmit={(e) => handleContactSubmit(e, 'talento')} className="space-y-5" data-testid="influencer-form">
                    <p className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Completá tus datos y al menos una red social. Los campos marcados con * son obligatorios.
                    </p>

                    {/* Identidad */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="talento-nombre" className="text-gray-700">Nombre y apellido *</Label>
                        <Input id="talento-nombre" name="nombre" placeholder="Tu nombre" required className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-nombre" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="talento-email" className="text-gray-700">Email *</Label>
                        <Input id="talento-email" name="email" type="email" placeholder="tu@email.com" required className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-email" />
                      </div>
                    </div>

                    {/* Ubicación + edad + celular */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="talento-pais" className="text-gray-700">País *</Label>
                        <Input id="talento-pais" name="pais" placeholder="Ej: Argentina" required className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-pais" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="talento-ciudad" className="text-gray-700">Ciudad</Label>
                        <Input id="talento-ciudad" name="ciudad" placeholder="Ej: Buenos Aires" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-ciudad" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="talento-celular" className="text-gray-700">Celular / WhatsApp *</Label>
                        <Input id="talento-celular" name="celular" placeholder="+54 9 11 ..." required className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-celular" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="talento-edad" className="text-gray-700">Edad</Label>
                        <Input id="talento-edad" name="edad" type="number" min="10" max="99" placeholder="Ej: 24" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-edad" />
                      </div>
                    </div>

                    {/* Redes sociales: usuario + seguidores */}
                    <div className="space-y-3 pt-2" id="influencer-socials-block">
                      <Label className="text-gray-700 text-base font-semibold">Tus redes sociales *</Label>
                      <p className="text-xs text-gray-500">Completá al menos <strong>una</strong> red con <strong>usuario + seguidores</strong>. Dejá vacías las que no usés.</p>

                      <div className="grid grid-cols-[110px_1fr_140px] gap-2 items-center" data-testid="influencer-row-instagram">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <Instagram size={16} style={{ color: '#E4405F' }} />Instagram
                        </div>
                        <Input name="instagram_handle" value={socials.instagram_handle} onChange={(e) => setSocial('instagram_handle', e.target.value)} placeholder="@usuario" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-ig-handle" />
                        <Input name="instagram_followers" value={socials.instagram_followers} onChange={(e) => setSocial('instagram_followers', e.target.value)} placeholder="Seguidores" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-ig-followers" />
                      </div>

                      <div className="grid grid-cols-[110px_1fr_140px] gap-2 items-center" data-testid="influencer-row-tiktok">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <FaTiktok size={14} style={{ color: '#000' }} />TikTok
                        </div>
                        <Input name="tiktok_handle" value={socials.tiktok_handle} onChange={(e) => setSocial('tiktok_handle', e.target.value)} placeholder="@usuario" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-tt-handle" />
                        <Input name="tiktok_followers" value={socials.tiktok_followers} onChange={(e) => setSocial('tiktok_followers', e.target.value)} placeholder="Seguidores" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-tt-followers" />
                      </div>

                      <div className="grid grid-cols-[110px_1fr_140px] gap-2 items-center" data-testid="influencer-row-youtube">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <FaYoutube size={16} style={{ color: '#FF0000' }} />YouTube
                        </div>
                        <Input name="youtube_handle" value={socials.youtube_handle} onChange={(e) => setSocial('youtube_handle', e.target.value)} placeholder="@canal o link" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-yt-handle" />
                        <Input name="youtube_followers" value={socials.youtube_followers} onChange={(e) => setSocial('youtube_followers', e.target.value)} placeholder="Suscriptores" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="influencer-input-yt-followers" />
                      </div>

                      {socialTouched && !hasCompleteSocial && (
                        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="influencer-socials-error">
                          <span className="mt-[1px]">⚠️</span>
                          <span>Falta completar al menos <strong>una red social</strong> con <strong>usuario y cantidad de seguidores</strong> para poder enviar tu solicitud.</span>
                        </div>
                      )}
                    </div>

                    {/* Verticales */}
                    <div className="space-y-2 pt-2">
                      <Label className="text-gray-700 text-base font-semibold">Verticales / Categorías</Label>
                      <p className="text-xs text-gray-500">Marcá las que mejor describen tu contenido.</p>
                      <div className="flex flex-wrap gap-2" data-testid="influencer-verticales">
                        {VERTICAL_OPTIONS.map((v) => {
                          const selected = talentoVerticales.includes(v);
                          return (
                            <button
                              type="button"
                              key={v}
                              onClick={() => toggleVertical(v)}
                              className="px-3 py-1.5 text-xs md:text-sm rounded-full border transition-all"
                              style={{
                                backgroundColor: selected ? '#F4A1C8' : 'white',
                                color: selected ? '#4A154B' : '#374151',
                                borderColor: selected ? '#F4A1C8' : '#d1d5db',
                              }}
                              data-testid={`vertical-chip-${v}`}
                            >
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Mensaje */}
                    <div className="space-y-2">
                      <Label htmlFor="talento-mensaje" className="text-gray-700">Contanos más sobre vos (opcional)</Label>
                      <Textarea id="talento-mensaje" name="mensaje" placeholder="¿Qué tipo de contenido hacés? ¿Con qué marcas trabajaste?" rows={4} className="bg-gray-50 border-gray-300 text-gray-900 resize-none" data-testid="influencer-input-mensaje" />
                    </div>

                    <Button type="submit" disabled={loading || !hasCompleteSocial} className="w-full font-semibold py-6 text-lg disabled:opacity-60 disabled:cursor-not-allowed" style={{ backgroundColor: '#F4A1C8', color: '#4A154B' }} data-testid="influencer-submit-btn">
                      {loading ? 'Enviando...' : !hasCompleteSocial ? 'Completá al menos una red social' : 'Enviar Solicitud'}
                    </Button>
                  </form>
                </CardContent></Card>
              </TabsContent>
            </Tabs>
          </div>
          )}
        </div>
      </section>

      {/* Brands Section */}
      <BrandsSection />

      {/* Floating Contact Button */}
      <a
        href="#contacto"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl"
        style={{ backgroundColor: '#F4A1C8' }}
        data-testid="floating-contact-button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4A154B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </a>

      {/* Talent Modal */}
      {selectedTalent && (
        <TalentModal talento={selectedTalent} onClose={() => setSelectedTalent(null)} />
      )}

      </div> {/* Cierre del Watermark Background Container */}
    </div>
  );
};

export default NewHome;
